import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { MilestoneEditor } from '../../components/Contracts/MilestoneEditor';
import { CreateMilestoneInput, EditContractInput, Milestone, ContractStatus, Contract } from '../../types/contract';
import { ArrowLeft, Save, AlertTriangle } from 'lucide-react';
import { useToast } from '../../components/UI/Toast';
import { contractService } from '../../services/contractService';
import { userService } from '../../services/userService';
import { canEditContract } from '../../utils/contractHelpers';

const EditContract = () => {
    const { showToast, ToastWrapper } = useToast();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [contract, setContract] = useState<Contract | null>(null);
    const [originalMilestones, setOriginalMilestones] = useState<Milestone[]>([]);
    const [userId, setUserId] = useState<string>('');

    const [formData, setFormData] = useState<EditContractInput & { milestones: CreateMilestoneInput[], currency: string }>({
        title: '',
        description: '',
        requirements: '',
        terms: '',
        total_amount: 0,
        currency: 'CNY',
        estimated_delivery_date: Date.now(),
        milestones: [],
    });

    useEffect(() => {
        const fetchUser = async () => {
            const userRes = await userService.getUserProfile();
            if (userRes.success && userRes.data) {
                setUserId(userRes.data.userProfile.userId);
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!id || !userId) return;
            setIsLoading(true);
            try {
                const res = await contractService.getContract(id);
                if (res.success && res.data) {
                    const { contract: fetchedContract, milestones: fetchedMilestones } = res.data;

                    if (!canEditContract(fetchedContract, userId)) {
                        showToast('你没有权限编辑此合同', 'error');
                        navigate(`/contracts/${id}`);
                        return;
                    }

                    setContract(fetchedContract);
                    setOriginalMilestones(fetchedMilestones);

                    const mappedMilestones: CreateMilestoneInput[] = fetchedMilestones.map(m => ({
                        title: m.title,
                        description: m.description,
                        order_index: m.order_index,
                        payment_amount: m.payment_amount,
                        requires_deliverable: m.requires_deliverable,
                        has_penalty: m.has_penalty,
                        penalty_type: m.penalty_type,
                        penalty_value: m.penalty_value,
                        penalty_description: m.penalty_description,
                        estimated_completion_date: m.estimated_completion_date,
                    }));

                    setFormData({
                        title: fetchedContract.title,
                        description: fetchedContract.description,
                        requirements: fetchedContract.requirements,
                        terms: fetchedContract.terms,
                        total_amount: fetchedContract.total_amount,
                        currency: fetchedContract.currency,
                        estimated_delivery_date: fetchedContract.estimated_delivery_date,
                        milestones: mappedMilestones,
                    });
                } else {
                    showToast('未找到合同', 'error');
                    navigate('/contracts');
                }
            } catch (err) {
                console.error('Fetch error:', err);
                showToast('合同加载失败', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        if (userId) {
            fetchData();
        }
    }, [id, userId, navigate, showToast]);

    // Identify locked milestones indices (e.g. Completed)
    const lockedIndices = useMemo(() => {
        return originalMilestones
            .map((m, index) => (m.status === 'COMPLETED' ? index : -1))
            .filter(i => i !== -1);
    }, [originalMilestones]);

    const handleMilestonesChange = (newMilestones: CreateMilestoneInput[]) => {
        const total = newMilestones.reduce((sum, m) => sum + m.payment_amount, 0);
        const lastDate = newMilestones.length > 0
            ? Math.max(...newMilestones.map(m => m.estimated_completion_date))
            : Date.now();

        setFormData(prev => ({
            ...prev,
            milestones: newMilestones,
            total_amount: total,
            estimated_delivery_date: lastDate
        }));
    };

    const handleSubmit = async (isDraft: boolean = false) => {
        if (!isDraft) {
            if (!formData.title || !formData.description) {
                showToast('请填写完整的合同基本信息', 'error');
                return;
            }

            if (formData.milestones && formData.milestones.length > 0) {
                const hasDeliverable = formData.milestones.some(m => m.requires_deliverable);
                if (!hasDeliverable) {
                    showToast('合同中必须至少包含一个需要交付物的里程碑', 'error');
                    return;
                }

                const hasPayment = formData.milestones.some(m => m.payment_amount > 0);
                if (!hasPayment) {
                    showToast('合同总金额不能为 0，请检查里程碑付款金额', 'error');
                    return;
                }
            }
        } else {
            if (!formData.title) {
                showToast('保存草稿至少需要填写标题', 'error');
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                status: (isDraft ? 'DRAFT' : 'PENDING_CLIENT_APPROVAL') as ContractStatus
            };
            const res = await contractService.updateContract(id!, payload);
            if (res.success) {
                showToast(isDraft ? '草稿已保存' : '合同已提交审核', 'success');
                setTimeout(() => navigate(isDraft ? '/contracts?view=creator' : `/contracts/${id}`), 1500);
            } else {
                showToast(res.error || '更新失败', 'error');
            }
        } catch (error) {
            console.error(error);
            showToast('更新失败', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-slate-500">加载中...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-20 px-6">
            <ToastWrapper />
            <div className="max-w-4xl mx-auto">
                <button
                    onClick={() => navigate(`/contracts/${id}`)}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">返回合同详情</span>
                </button>

                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="border-b border-slate-200 p-8">
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-2xl font-bold text-slate-900">编辑合同</h1>
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-md border border-amber-200">
                                编辑后需重新审核
                            </span>
                        </div>
                        <p className="text-slate-600">修改合同详情或里程碑</p>
                    </div>

                    <div className="bg-amber-50 px-8 py-4 border-b border-amber-100 flex items-center gap-3 text-amber-800 text-sm">
                        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                        <p>注意：修改合同会导致状态变更为“待审核”，且在客户确认前暂停执行。已完成的里程碑无法修改。</p>
                    </div>

                    <div className="p-8 space-y-8">
                        {/* Basic Info */}
                        <section className="space-y-6">
                            <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-100 pb-2">基本信息</h3>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">合同标题</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">项目描述</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    rows={3}
                                    className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-none"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">项目要求</label>
                                    <textarea
                                        value={formData.requirements}
                                        onChange={(e) => setFormData(prev => ({ ...prev, requirements: e.target.value }))}
                                        rows={4}
                                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">合同条款</label>
                                    <textarea
                                        value={formData.terms}
                                        onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                                        rows={4}
                                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-none"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Milestones */}
                        <section className="space-y-6">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                <h3 className="text-lg font-semibold text-slate-900">项目里程碑</h3>
                                <div className="text-right">
                                    <p className="text-xs text-slate-500">总金额</p>
                                    <p className="text-xl font-bold text-slate-900">
                                        {formData.currency === 'CNY' ? '¥' : '$'}
                                        {formData.total_amount?.toLocaleString() ?? 0}
                                    </p>
                                </div>
                            </div>

                            <MilestoneEditor
                                milestones={formData.milestones}
                                onChange={handleMilestonesChange}
                                currency={formData.currency}
                                disabledMilestoneIndices={lockedIndices}
                            />
                        </section>
                    </div>

                    <div className="border-t border-slate-200 p-8 bg-slate-50 flex justify-end gap-4">
                        <Button variant="outline" onClick={() => navigate(`/contracts/${id}`)}>取消</Button>
                        <Button variant="outline" onClick={() => handleSubmit(true)} disabled={isSubmitting}>
                            <Save className="w-5 h-5 mr-2" />
                            保存草稿
                        </Button>
                        <Button variant="primary" onClick={() => handleSubmit(false)} disabled={isSubmitting}>
                            {isSubmitting ? '保存中...' : (
                                <>
                                    <Save className="w-5 h-5 mr-2" />
                                    保存并提交审核
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditContract;
